import React, {useRef, useState} from 'react';
import {Line} from 'react-chartjs-2';
import {convertNumber} from '../../../../util/DominantUtil';
import MarketStyle from "../../market/MarketStyle";

function makeArray(data, name, change) {
    if (change) return Object.entries(data).map(([k, v]) => v.data[name] * (change[k] < 0 ? -1 : 1))

    return data ? Object.values(data).map(key => key.data[name]) : null;
}

export default function ChartUi({data, sectors, byValue}) {
    const chartComponent = useRef(null);
    const [change] = useState(makeArray(data, "change"));
    const RialSymbolImage = "/images/RiyalSymbol-small-white.png";

    const chartSeries = {
        labels: sectors,
        datasets: [
            {
                label: 'خرید حقیقی',
                backgroundColor: '#9CCC65',
                borderColor: '#fff',
                lineTension: 0,
                borderWidth: 1.5,
                pointRadius: 0,
                data: makeArray(data, "individualsBuy", change)
            }, {
                label: 'فروش حقیقی',
                backgroundColor: '#EF5350',
                borderColor: '#fff',
                lineTension: 0,
                borderWidth: 1.5,
                pointRadius: 0,
                data: makeArray(data, "individualsSell", change)
            }, {
                label: 'خرید حقوقی',
                backgroundColor: '#42A5F5',
                stack: "Base",
                borderColor: '#fff',
                lineTension: 0,
                borderWidth: 1.5,
                pointRadius: 0,
                data: makeArray(data, "companiesBuy", change)
            }, {
                label: 'فروش حقوقی',
                backgroundColor: '#FFA000',
                borderColor: '#fff',
                lineTension: 0,
                borderWidth: 1.5,
                pointRadius: 0,
                data: makeArray(data, "companiesSell", change),
            }, {
                label: 'تغییر',
                type: 'line',
                backgroundColor: 'white',
                borderColor: 'rgba(250,250,250,.1)',
                lineTension: 0,
                borderWidth: 0,
                pointRadius: 0,
                data: change,
                id: 'y-axis-2',
            }
        ],
    };

    const options = {
        responsive: true,
        legend: {
            display: false
        },
        maintainAspectRatio: false,
        tooltips: {
            mode: "index",
            intersect: false,
            enabled: false,
            position: "nearest",
            custom: function (tooltipModel) {
                // Tooltip Element
                var tooltipEl = document.getElementById('chartjs-tooltip');

                // Create element on first render
                if (!tooltipEl) {
                    tooltipEl = document.createElement('div');
                    tooltipEl.id = 'chartjs-tooltip';
                    tooltipEl.innerHTML = '<div class=".tooltip-box"></div>';
                    document.body.appendChild(tooltipEl);
                }

                // Hide if no tooltip
                if (tooltipModel.opacity === 0) {
                    tooltipEl.style.opacity = 0;
                    return;
                }

                // Set caret Position
                tooltipEl.classList.remove('above', 'below', 'no-transform');
                if (tooltipModel.yAlign) {
                    tooltipEl.classList.add(tooltipModel.yAlign);
                } else {
                    tooltipEl.classList.add('no-transform');
                }

                function getBody(bodyItem) {
                    return bodyItem.lines;
                }

                // Set Text
                if (tooltipModel.body) {
                    const titleLines = tooltipModel.title || [];
                    const bodyLines = tooltipModel.body.map(getBody);

                    let innerHtml = '';

                    titleLines.forEach(function (title) {
                        innerHtml += '<div class="tooltip-title"><span>' + title + '</span></div>';
                    });

                    bodyLines.forEach(function (body, i) {
                        const dataset = body[0].split(":")
                        const label = dataset[0]
                        const lastData = parseFloat(dataset[1].trim())
                        const colors = tooltipModel.labelColors[i];
                        const span = '<span class="colored-rectangle" style="' + 'background:' + colors.backgroundColor + '"></span>';
                        const title = '<span>' + label + '</span>'
                        const mappedDataSpan = '<span class="ltr">' + (label === 'تغییر' ? lastData.toFixed(2) : byValue ? convertNumber(lastData, 0) : convertNumber(lastData, 2)) + '</span>'
                        const suffixSpan = label === 'تغییر' ? ' درصد' : byValue ? '<img class="tooltip-currency-symbol" alt="IRR" src="' + RialSymbolImage + '" width="12" height="13" class="mr-1px mb-1px" />' : ''
                        innerHtml += '<div><div class="tooltip-value">' + span + ' ' + title + " " + mappedDataSpan + suffixSpan + '</div></div>';
                    });

                    const tableRoot = tooltipEl.querySelector('div');
                    tableRoot.innerHTML = innerHtml;
                }

                // `this` will be the overall tooltip
                const position = this._chart.canvas.getBoundingClientRect();

                // Display, position, and set styles for font
                tooltipEl.style.opacity = 1;
                tooltipEl.style.position = 'absolute';
                tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
                tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
                tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
                tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px';
                tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
                tooltipEl.style.padding = tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px';
                tooltipEl.style.pointerEvents = 'none';
            },
        },
        scales: {
            yAxes: [{
                position: 'right',
                stacked: true,
                display: true,
                ticks: {
                    callback: function (value) {
                        return convertNumber(value);
                    }
                },
            }, {
                type: 'linear',
                display: false,
                ticks: {
                    max: 6,
                },
                id: 'y-axis-2',
            }],
            xAxes: [{
                display: true,
                ticks: {
                    minRotation: 45,
                },
                scaleLabel: {
                    display: true,
                },
                gridLines: {
                    color: "rgba(0, 0, 0, 0)",
                }
            }],
        }, plugins: {
            threshold: false   // disable plugin 'p1' for this instance
        }
    }

    return (
        <div style={{height: '400px'}}>
            <div style={{display: "flex", flexDirection: "row", margin: '0 37px 0 38px'}}>
                <span className="col-4 live-table-title">بر اساس {!byValue ? 'حجم' : 'ارزش'} معاملات</span>
                <div className="col-8 range-selectors" dir="rtl">
                    <span className="date-range-btn disabled" id="all">همه</span>
                    <span className="date-range-btn disabled" id="5y">5 سال</span>
                    <span className="date-range-btn disabled" id="1y">1 سال</span>
                    <span className="date-range-btn disabled" id="6m">6 ماه</span>
                    <span className="date-range-btn disabled" id="1m">1 ماه</span>
                    <span className="date-range-btn disabled" id="5d">5 روز</span>
                    <span className="date-range-btn disabled" id="1d">1 روز</span>
                </div>
            </div>
            <Line
                width={'100%'}
                height={400}
                data={chartSeries}
                ref={chartComponent}
                options={options}
            />

            <MarketStyle/>
        </div>
    );
};
